import React, { useEffect, useRef, useState } from "react";
import configDetails from "../Config/Config";
import { BsMicFill, BsArrowUpCircleFill } from 'react-icons/bs';

function NoteforgeAiChat() {
  const API_GATEWAY_URL = configDetails.chatbotApiUrl;

  const [messages, setMessages] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("initializing");
  const [userName, setUserName] = useState("You");

  const sessionIdRef = useRef(sessionStorage.getItem("sessionId") || generateUUID());
  const userIdRef = useRef(localStorage.getItem("userId") || 1);
  const chatEndRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem("sessionId", sessionIdRef.current);
    localStorage.setItem("userId", userIdRef.current);

    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.name) setUserName(storedUser.name);

    checkConnection().then((ok) => setConnectionStatus(ok ? "connected" : "error"));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const checkConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(API_GATEWAY_URL, {
        method: "OPTIONS",
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  };

  const handleSearchSubmit = async () => {
    const prompt = searchInput.trim();
    if (!prompt) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    setSearchInput("");
    const userMessage = {
      type: "user",
      name: userName,
      avatar: getInitials(userName),
      text: prompt,
      timestamp,
    };
    setMessages((prev) => [...prev, userMessage]);
    setConnectionStatus("sending");

    try {
      const response = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          session_id: sessionIdRef.current,
          user_id: userIdRef.current,
        }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const data = await response.json();

      const botMessage = {
        type: "bot",
        name: "NoteForge AI",
        avatar: "🤖",
        text: cleanBotResponse(data.response),
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };

      setMessages((prev) => [...prev, botMessage]);
      setConnectionStatus("response-received");
    } catch (err) {
      const errorMsg = {
        type: "bot",
        name: "NoteForge AI",
        avatar: "🤖",
        text: `❌ ${err.message}`,
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      setMessages((prev) => [...prev, errorMsg]);
      setConnectionStatus("error");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return alert("Your browser doesn't support voice input.");

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = "";
    setConnectionStatus("listening");

    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; ++i) {
        const transcript = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscript += transcript + " ";
        else interim += transcript;
      }
      setSearchInput(finalTranscript + interim);
    };

    recognition.onerror = (e) => {
      console.error("Voice Error:", e.error);
      recognition.stop();
      setConnectionStatus("error");
    };

    recognition.onend = () => {
      if (connectionStatus !== "error") setConnectionStatus("connected");
    };

    recognition.start();
    setTimeout(() => recognition.stop(), 30000);
  };

  const cleanBotResponse = (text) =>
    text
      .replace(/^#+\s?/gm, "")
      .replace(/\*{1,3}/g, "")
      .replace(/[_~`>]+/g, "")
      .replace(/^\s*[-+*]\s+/gm, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const statusMessage = {
    initializing: "⏳ Connecting...",
    connected: "✅ Connected to NoteForge AI",
    sending: "⏳ Sending your question...",
    "response-received": "✅ Response received",
    listening: "🎙️ Listening...",
    error: "❌ Connection or response error",
  };

  return (
    <div className="container-fluid py-4">
      <h1 className="text-center mb-3 fw-bold text-3xl">NoteForge AI Chat</h1>
      <p className={`fw-semibold mb-3 ${connectionStatus.includes("error") ? "text-danger" : "text-secondary"}`}>
        {statusMessage[connectionStatus]}
      </p>

      <div className="border rounded p-3 mb-3" style={{ height: "65vh", overflowY: "auto", background: "#fefefe" }}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`d-flex mb-3 ${msg.type === "user" ? "justify-content-end text-end" : "justify-content-start text-start"}`}
          >
            {msg.type === "bot" ? (
              <>
                <img
                  src="/Images/NoteForgeBot.png"
                  alt="Bot"
                  style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", marginRight: "0.5rem" }}
                />
                <div>
                  <div className="fw-semibold">
                    {msg.name}{" "}
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                      • {msg.timestamp}
                    </span>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="fw-semibold">
                    {msg.name}{" "}
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                      • {msg.timestamp}
                    </span>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
                </div>
                <div
                  className="bg-primary text-white d-flex align-items-center justify-content-center ms-2"
                  style={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  {msg.avatar}
                </div>
              </>
            )}
          </div>
        ))}
        <div ref={chatEndRef}></div>
      </div>

      <div className="d-flex justify-content-center align-items-center">
        <div className="position-relative" style={{ width: "60%", minWidth: "300px" }}>
          <textarea
            className="form-control rounded-pill py-3 ps-5 pe-5"
            placeholder="Ask NoteForge AI..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            style={{
              resize: "none",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              maxHeight: "150px",
              lineHeight: "1.5",
            }}
          />
          <button
            onClick={handleVoiceInput}
            className="position-absolute top-50 start-0 translate-middle-y ms-3 border-0 bg-transparent"
          >
            <BsMicFill style={{ fontSize: "1.3rem", color: "#000" }} />
          </button>
          <button
            onClick={handleSearchSubmit}
            disabled={!searchInput.trim()}
            className="position-absolute top-50 end-0 translate-middle-y me-3 border-0 bg-transparent"
            style={{ cursor: "pointer" }}
          >
            <BsArrowUpCircleFill style={{ fontSize: "2rem", color: "#60a5fa" }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getInitials(name) {
  return name ? name.trim().charAt(0).toUpperCase() : "U";
}

export default NoteforgeAiChat;